import {
  QuestionType,
  type QuestionOption,
  type SurveyQuestion,
  type SurveySection,
  type SurveyPage,
  SurveyResponseStatus,
} from '@gokrew/models/survey';
import { Tone } from '@gokrew/models/ui.types';
import type {
  SurveyAnswerValue,
  RawOption,
  RawQuestion,
  RawSection,
  RawPage,
  RawSavedAnswer,
} from '@/types/employee-survey';

// ─── Availability ───────────────────────────────────────────────────────────
function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

export function isOverdue(endsOn: string | null | undefined) {
  if (!endsOn) return false;
  return endsOn.slice(0, 10) < todayDateString();
}

export function isUpcoming(startsOn: string | null | undefined) {
  if (!startsOn) return false;
  return startsOn.slice(0, 10) > todayDateString();
}

// Moved out of SurveyStatusChip.tsx — a component file can only export
// components, or Fast Refresh stops working for it.
export function getSurveyStatusTone({
  status,
  overdue,
  closed,
  reopened,
  upcoming,
}: {
  status: SurveyResponseStatus;
  overdue: boolean;
  closed: boolean;
  reopened: boolean;
  upcoming: boolean;
}): Tone | null {
  if (status === SurveyResponseStatus.COMPLETED) return Tone.SUCCESS;
  if (status === SurveyResponseStatus.EXPIRED) return null;
  if (closed) return null;
  if (upcoming) return null;
  if (overdue) return Tone.ERROR;
  if (reopened) return Tone.ACCENT;
  return status === SurveyResponseStatus.IN_PROGRESS
    ? Tone.WARNING
    : Tone.PRIMARY;
}

// ─── Mapping (mirrors frontend/src/utils/survey.ts) ────────────────────────

export function mapQuestionTypeFromApi(raw: RawQuestion) {
  if (raw.type === 'rating_5') {
    return { type: QuestionType.RATING, ratingConfig: { maxRating: 5 } };
  }
  if (raw.type === 'rating_10') {
    return { type: QuestionType.RATING, ratingConfig: { maxRating: 10 } };
  }
  return { type: raw.type as SurveyQuestion['type'], ratingConfig: undefined };
}

export function mapQuestionConfigFromApi(
  type: SurveyQuestion['type'],
  config: Record<string, unknown>
): Partial<SurveyQuestion> {
  if (!config || Object.keys(config).length === 0) return {};
  switch (type) {
    case 'number':
      return { numberConfig: config as never };
    case 'file_upload':
      return { fileUploadConfig: config as never };
    case 'image_upload':
      return { imageUploadConfig: config as never };
    case 'linear_scale':
      return { linearScaleConfig: config as never };
    case 'short_text':
    case 'paragraph':
      return { textConfig: config as never };
    case 'date':
      return { dateConfig: config as never };
    default:
      return {};
  }
}

export function mapOptionFromApi(raw: RawOption): QuestionOption {
  return {
    id: raw.id,
    label: raw.label,
    description: raw.value ?? undefined,
    imageUrl: raw.imageKey ?? undefined,
  };
}

export function mapQuestionFromApi(raw: RawQuestion): SurveyQuestion {
  const { type, ratingConfig } = mapQuestionTypeFromApi(raw);
  return {
    id: raw.id,
    type,
    title: raw.title,
    description: raw.description ?? undefined,
    // Lives inside the generic `config` blob on the wire, not a dedicated
    // column — mirrors frontend/src/utils/survey.ts's mapQuestionFromApi.
    placeholder:
      (raw.config as { placeholder?: string } | undefined)?.placeholder ??
      undefined,
    required: raw.isRequired,
    helpText: raw.helpText ?? undefined,
    order: raw.orderIndex,
    options: raw.options.length
      ? [...raw.options]
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map(mapOptionFromApi)
      : undefined,
    ratingConfig,
    ...mapQuestionConfigFromApi(type, raw.config ?? {}),
  };
}

export function mapSectionFromApi(raw: RawSection): SurveySection {
  return {
    id: raw.id,
    title: raw.title,
    description: raw.description ?? undefined,
    order: raw.orderIndex,
    questions: [...raw.questions]
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map(mapQuestionFromApi),
  };
}

export function mapPageFromApi(raw: RawPage): SurveyPage {
  const sections = [...raw.sections].sort(
    (a, b) => a.orderIndex - b.orderIndex
  );
  const onlyImplicit =
    sections.length <= 1 && sections.every((s) => s.isImplicit);

  if (onlyImplicit) {
    return {
      id: raw.id,
      title: raw.title,
      description: raw.description ?? undefined,
      order: raw.orderIndex,
      sectionsEnabled: false,
      questions: [...(sections[0]?.questions ?? [])]
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map(mapQuestionFromApi),
      sections: [],
    };
  }

  return {
    id: raw.id,
    title: raw.title,
    description: raw.description ?? undefined,
    order: raw.orderIndex,
    sectionsEnabled: true,
    questions: [],
    sections: sections.map(mapSectionFromApi),
  };
}

export function collectQuestionTypes(
  pages: SurveyPage[]
): Map<string, QuestionType> {
  const map = new Map<string, QuestionType>();
  for (const page of pages) {
    const questionLists = page.sectionsEnabled
      ? page.sections.map((s) => s.questions)
      : [page.questions];
    for (const questions of questionLists) {
      for (const q of questions) map.set(q.id, q.type);
    }
  }
  return map;
}

export function mapSavedAnswerToValue(
  type: QuestionType | undefined,
  raw: RawSavedAnswer
): SurveyAnswerValue {
  switch (type) {
    case 'short_text':
    case 'paragraph':
    case 'email':
    case 'phone':
      return raw.textValue;
    case 'number':
    case 'rating':
    case 'linear_scale':
      return raw.numericValue;
    case 'date':
      return raw.dateValue;
    case 'time':
      return raw.timeValue;
    case 'yes_no':
      return raw.booleanValue === undefined
        ? undefined
        : raw.booleanValue
          ? 'Yes'
          : 'No';
    case 'single_choice':
    case 'dropdown':
    case 'image_choice':
      return raw.selectedOptionIds[0];
    case 'multiple_choice':
      return raw.selectedOptionIds;
    // file_upload/image_upload answers are tracked separately (files[]),
    // not through this flat value map.
    default:
      return undefined;
  }
}
